import { useEffect, useState, useContext, useCallback } from "react";
import { localStorage, ViewerContext } from "dash-ui";
import { AccountStatusesEnum } from "dash-common";
import {
  useGetCurrentAccountTrialProductSubscriptionsQuery,
  useGetCurrentAccountProductSubscriptionQuery,
  useGetCurrentBrokerQuery,
  useObserveCurrentAccountChangesSubscription,
  useObserveCurrentAccountProductSubscriptionSubscription,
  useObserveSubscriptionProductsChangesSubscription,
  useGetAccountQuery,
} from "gql/generated.graphql";
import { useLogout } from "common/hooks/useLogout/useLogout";
import * as Sentry from "@sentry/react";

const useInitViewer = () => {
  const [isReady, setIsReady] = useState(false);
  const {
    tokens,
    setAccount,
    setBroker,
    setProfile,
    setTokens,
    setSubscription,
    setUserHasTrialed,
  } = useContext(ViewerContext);
  const { accessToken } = tokens;
  const {
    loading: isViewerLoading,
    data: accountData,
    error,
  } = useGetCurrentBrokerQuery({
    skip: !accessToken,
    fetchPolicy: "network-only",
  });
  const broker = accountData?.getCurrentBroker;
  const { account, profile, subscriptionType } = broker || {};
  const [logout] = useLogout();

  const { data: ssoAccountData, loading: isSSOLoading } = useGetAccountQuery({
    skip: !!account || !accessToken,
  });

  const ssoAccount = ssoAccountData?.getAccount;

  const {
    data: subscriptionData,
    loading: isViewerSubscriptionLoading,
    refetch: subscriptionRefetch,
  } = useGetCurrentAccountProductSubscriptionQuery({
    skip: !accessToken,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const subscription = subscriptionData?.getCurrentAccountProductSubscription;

  const {
    data: trialSubscriptionsData,
    loading: isViewerTrialSubscriptionsLoading,
    refetch: trialSubscriptionsRefetch,
  } = useGetCurrentAccountTrialProductSubscriptionsQuery({
    skip: !accessToken,
    fetchPolicy: "network-only",
  });
  const userHasTrialed = // FIXME: this doesn't support trial by product. change in the future.
    !!trialSubscriptionsData?.getCurrentAccountTrialProductSubscriptions
      ?.collection.length;

  useObserveSubscriptionProductsChangesSubscription({
    onData() {
      subscriptionRefetch();
      trialSubscriptionsRefetch();
    },
  });

  useObserveCurrentAccountProductSubscriptionSubscription({
    onData({ data }) {
      if (setSubscription) {
        setSubscription(
          data.data?.observeCurrentAccountProductSubscription.document
        );
      }
    },
  });

  useObserveCurrentAccountChangesSubscription({
    onData: ({ data }) => {
      const account = data?.data?.observeCurrentAccountChanges?.account;
      if (account) {
        if (account.status !== AccountStatusesEnum.ACTIVE) {
          logout();
          return;
        }
        if (setAccount) {
          setAccount(account);
        }
      }
    },
  });

  // FIXME: re-check next line
  useEffect(() => {
    if (
      !isViewerLoading &&
      !isViewerSubscriptionLoading &&
      !isViewerTrialSubscriptionsLoading &&
      !isSSOLoading
    ) {
      if (account && setAccount) {
        setAccount(account);
      }
      if (ssoAccount && setAccount) {
        setAccount(ssoAccount);
      }
      if (profile && setProfile) {
        setProfile(profile);
      }
      if (broker && setBroker) {
        setBroker(broker);
        Sentry.setUser({
          id: broker._id,
          email: account?.email,
          subscriptionType,
        });
      }
      if (subscription && setSubscription) {
        setSubscription(subscription);
      }
      if (userHasTrialed && setUserHasTrialed) {
        setUserHasTrialed(userHasTrialed);
      }
      if (error) {
        if (error.message === "Unauthorized") {
          logout();
          return;
        }
        console.error("---useInitUser -->", error);
      }
      // Defer isReady to make sure the context is updated when ProtectedRoute is rendered
      // This should be just a temporary solution as it is not reliable
      setTimeout(() => setIsReady(true), 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isViewerLoading,
    isViewerSubscriptionLoading,
    isViewerTrialSubscriptionsLoading,
    setAccount,
    account,
    ssoAccount,
    broker,
    setBroker,
    profile,
    setProfile,
    subscription,
    setSubscription,
    userHasTrialed,
    setUserHasTrialed,
    error,
  ]);

  const onLocalStorageChanged = useCallback(() => {
    if (setTokens) {
      setTokens({
        accessToken: localStorage.getAccessToken() || "",
        refreshToken: localStorage.getRefreshToken() || "",
        refreshTokenExpiry: localStorage.getRefreshTokenExpiry() || "",
      });
    }
  }, [setTokens]);

  useEffect(() => {
    window.addEventListener("storage", onLocalStorageChanged);
    return () => {
      window.removeEventListener("storage", onLocalStorageChanged);
    };
  }, [onLocalStorageChanged]);
  return [isReady];
};
export default useInitViewer;
